import { useReadContract, useReadContracts } from "wagmi";
import { useMemo } from "react";
import { PIGGY_ORIGINALS_ABI } from "@/lib/abis";
import { ADDRESSES } from "@/lib/config";

export interface GalleryItem {
    tokenId: bigint;
    rarity: number; // 0 = unrevealed, 1..6 = PIGLET..COSMIC_PIGGY
}

/**
 * Lightweight gallery feed — the most recently minted tokens, with their
 * on-chain rarity. Reads totalMinted, then fans out `rarityOf` for the last
 * `limit` token IDs. Each card resolves its image off /nft-img/{id}.png
 * (or whatever's been pointed at via setBaseURI).
 */
export function useRecentMints(limit = 24): {
    items: GalleryItem[];
    totalMinted: number;
    loading: boolean;
} {
    const contract = ADDRESSES.PIGGY_ORIGINALS;

    const totalReq = useReadContract({
        address: contract,
        abi: PIGGY_ORIGINALS_ABI,
        functionName: "totalMinted",
        query: { enabled: Boolean(contract), refetchInterval: 30_000 },
    });

    const totalMinted = Number((totalReq.data as bigint | undefined) ?? 0n);

    // Show the newest tokens first, oldest last; clamp to what exists.
    const ids = useMemo(() => {
        const count = Math.min(limit, totalMinted);
        const start = Math.max(1, totalMinted - count + 1);
        const arr: bigint[] = [];
        for (let i = totalMinted; i >= start; i--) arr.push(BigInt(i));
        return arr;
    }, [limit, totalMinted]);

    const rarities = useReadContracts({
        allowFailure: true,
        contracts: contract
            ? ids.map((id) => ({
                  address: contract,
                  abi: PIGGY_ORIGINALS_ABI,
                  functionName: "rarityOf" as const,
                  args: [id],
              }))
            : [],
        query: { enabled: Boolean(contract && ids.length > 0), refetchInterval: 30_000 },
    });

    const items: GalleryItem[] = ids.map((id, i) => ({
        tokenId: id,
        rarity: Number((rarities.data?.[i]?.result as number | undefined) ?? 0),
    }));

    return {
        items,
        totalMinted,
        loading: totalReq.isLoading || rarities.isLoading,
    };
}
